import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const values = [
  {
    icon: 'ri-leaf-line',
    title: 'Consciencia',
    description: 'El cambio real comienza con entender el impacto de nuestras acciones. Formamos ciudadanos que actúan con responsabilidad ambiental.',
    photo: 'https://readdy.ai/api/search-image?query=close%20up%20of%20green%20tropical%20leaves%20with%20morning%20dew%20drops%20in%20Colombian%20forest%2C%20vibrant%20nature%2C%20warm%20sunlight%20filtering%20through%20canopy%2C%20macro%20photography%2C%20lush%20vegetation%2C%20Santander%20Colombia%20jungle&width=500&height=400&seq=val_conc01&orientation=landscape',
  },
  {
    icon: 'ri-heart-line',
    title: 'Compromiso',
    description: 'Desde 2022 trabajamos sin parar en los 87 municipios de Santander. Prometemos resultados medibles y acompañamiento duradero.',
    photo: 'https://readdy.ai/api/search-image?query=volunteer%20team%20working%20together%20cleaning%20urban%20park%20in%20Bucaramanga%20Colombia%2C%20group%20of%20people%20picking%20up%20waste%2C%20community%20effort%2C%20warm%20golden%20hour%20light%2C%20diverse%20people%20smiling%2C%20realistic%20photography%20Santander&width=500&height=400&seq=val_comp01&orientation=landscape',
  },
  {
    icon: 'ri-group-line',
    title: 'Comunidad',
    description: 'El reciclaje es un acto colectivo. Construimos redes de personas comprometidas con el bienestar ambiental de Santander.',
    photo: 'https://readdy.ai/api/search-image?query=Colombian%20neighborhood%20community%20gathering%20in%20a%20clean%20public%20plaza%2C%20families%20talking%20and%20children%20playing%20near%20benches%20made%20of%20recycled%20plastic%20lumber%2C%20warm%20afternoon%20light%2C%20Bucaramanga%20Santander%20urban%20scene&width=500&height=400&seq=val_comm01&orientation=landscape',
  },
  {
    icon: 'ri-recycle-line',
    title: 'Transformación',
    description: 'Transformamos residuos en recursos, zonas contaminadas en parques, y materiales plásticos en madera útil para la comunidad.',
    photo: 'https://readdy.ai/api/search-image?query=recycled%20plastic%20lumber%20boards%20and%20finished%20benches%20in%20a%20workshop%20in%20Colombia%2C%20colorful%20plastic%20wood%20products%2C%20industrial%20transformation%20of%20waste%20into%20useful%20furniture%2C%20realistic%20photography%2C%20Santander&width=500&height=400&seq=val_trans01&orientation=landscape',
  },
];

const team = [
  {
    name: 'José Guillermo Núñez Rodríguez',
    role: 'Representante Legal',
    bio: 'Líder y fundador de la Fundación Frais. Desde 2022 impulsa la transformación ambiental y social en los 87 municipios de Santander con visión, compromiso y acción inmediata.',
    image: 'https://static.readdy.ai/image/04072f05a7ab05cce1b5fc08d2859ba4/104cfcbf74b1f6f35d546a0c3676ec16.jpeg',
    icon: 'ri-shield-star-line',
    color: '#1b5e20',
  },
  {
    name: 'José Emmanuel Núñez García',
    role: 'Secretario',
    bio: 'Apoya la gestión administrativa y operativa de la fundación. Coordina los programas de recolección, madera plástica posconsumo y alianzas con instituciones educativas de Santander.',
    image: 'https://static.readdy.ai/image/04072f05a7ab05cce1b5fc08d2859ba4/d0d5cb18098202f00babbb816a9943a8.jpeg',
    icon: 'ri-user-star-line',
    color: '#2e7d32',
  },
];

const milestones = [
  {
    year: '2022',
    title: 'Fundación y primer impacto',
    event: 'Nace la Fundación de Reciclaje en Acción Inmediata de Santander. Primeras jornadas de recolección de muebles, llantas y residuos voluminosos en Bucaramanga.',
    color: '#43a047',
  },
  {
    year: '2023',
    title: 'Expansión a nuevos municipios',
    event: 'Expansión del programa a más de 20 municipios de Santander. Inicio del programa de madera plástica posconsumo. Primeras alianzas con colegios y universidades.',
    color: '#2e7d32',
  },
  {
    year: '2024',
    title: 'Crecimiento y reconocimiento',
    event: 'Presencia activa en los 87 municipios de Santander. Más de 320 instituciones vinculadas. Programa RAEE de aparatos electrónicos en colegios y universidades.',
    color: '#1b5e20',
  },
  {
    year: '2025',
    title: 'Consolidación del movimiento',
    event: 'Más de 18.000 personas conscientizadas. Recuperación de alamedas, canchas y zonas verdes con materiales reciclados. Red de voluntarios activa en todo el departamento.',
    color: '#c62828',
  },
];

export default function AboutPage() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            (entry.target as HTMLElement).style.opacity = '1';
            (entry.target as HTMLElement).style.transform = 'translateY(0)';
          }
        });
      },
      { threshold: 0.1 }
    );
    const els = sectionRef.current?.querySelectorAll('.animate-in');
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <main ref={sectionRef}>

      {/* ── HERO ── */}
      <section className="relative w-full overflow-hidden" style={{ minHeight: '72vh' }}>
        {/* Real photo background */}
        <img
          src="https://storage.readdy-site.link/project_files/99b3bf7a-f106-44a8-ac25-700226859767/5db277f2-f0e0-44b3-8da0-2a5648c0c8b1_WhatsApp-Image-2026-04-19-at-12.04.00.jpeg?v=03d797d887b63d9d51b5d0d5df8ed91d"
          alt="Fundación Frais - Realidad ambiental en Santander"
          className="absolute inset-0 w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0d0d0d] via-[#0d0d0d]/55 to-[#0d0d0d]/30"></div>

        <div className="relative z-10 w-full px-6 md:px-14 pb-20 pt-44 flex flex-col justify-end" style={{ minHeight: '72vh' }}>
          <span
            className="inline-block text-[#a5d6a7] text-[10px] tracking-[0.5em] uppercase font-semibold mb-4"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Fundación de Reciclaje en Acción Inmediata de Santander
          </span>
          <h1
            className="font-bold text-white leading-tight mb-4"
            style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: 'clamp(2.8rem, 6vw, 5.5rem)',
            }}
          >
            Quiénes somos<br />
            <em className="not-italic" style={{ color: '#a5d6a7' }}>y por qué lo hacemos.</em>
          </h1>
          <p
            className="text-white/60 text-base max-w-2xl leading-relaxed"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Detrás de cada zona recuperada, cada llanta recogida y cada parque restaurado, 
            hay personas que decidieron actuar. Conoce a quienes mueven la Fundación Frais desde 2022.
          </p>
        </div>
      </section>

      {/* ── HISTORIA / MISIÓN ── */}
      <section className="py-24 md:py-32 px-6 md:px-14 bg-white">
        <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div
            className="animate-in"
            style={{ opacity: 0, transform: 'translateY(30px)', transition: 'opacity 0.8s ease, transform 0.8s ease' }}
          >
            <span
              className="inline-block text-[#43a047] text-[10px] tracking-[0.45em] uppercase font-semibold mb-4"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Nuestra Historia
            </span>
            <h2
              className="text-[#1b5e20] font-bold leading-tight mb-6"
              style={{
                fontFamily: "'Playfair Display', serif",
                fontSize: 'clamp(2rem, 3.5vw, 3rem)',
              }}
            >
              Nacimos en Santander<br />para cuidar a Santander
            </h2>
            <p
              className="text-[#4a5568] text-base leading-relaxed mb-5"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              La Fundación de Reciclaje en Acción Inmediata de Santander nació en <strong>2022</strong> con una certeza: 
              los residuos no son un problema, son una oportunidad. Sofás abandonados en zonas verdes, llantas 
              tiradas junto a ríos, plásticos acumulados en andenes — todo eso podía transformarse.
            </p>
            <p
              className="text-[#4a5568] text-base leading-relaxed mb-10"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Hoy operamos en los <strong>87 municipios del departamento</strong>, recuperamos espacios públicos, 
              transformamos plástico posconsumo en madera plástica y formamos comunidades que actúan. 
              Cada día que pasa sin actuar, el daño se acumula. Por eso somos de acción inmediata.
            </p>

            <div className="grid grid-cols-3 gap-6 pt-6 border-t border-[#e5e7eb]">
              {[
                { value: '87', label: 'Municipios activos' },
                { value: '18K+', label: 'Personas impactadas' },
                { value: '320+', label: 'Instituciones aliadas' },
              ].map((s) => (
                <div key={s.label}>
                  <div
                    className="text-[#1b5e20] font-bold text-3xl mb-1"
                    style={{ fontFamily: "'Playfair Display', serif" }}
                  >
                    {s.value}
                  </div>
                  <div
                    className="text-[#9ca3af] text-xs uppercase tracking-wide"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                  >
                    {s.label}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Real waste photo */}
          <div
            className="animate-in relative rounded-2xl overflow-hidden"
            style={{ height: '480px', opacity: 0, transform: 'translateY(30px)', transition: 'opacity 0.8s ease 0.2s, transform 0.8s ease 0.2s' }}
          >
            <img
              src="https://storage.readdy-site.link/project_files/99b3bf7a-f106-44a8-ac25-700226859767/0754966e-ca48-4a79-bc2d-b66522853d1a_WhatsApp-Image-2026-04-19-at-12.04.02.jpeg?v=973e874e32405338012e8b5617fe1cd3"
              alt="Zona contaminada en Santander antes de intervención Fundación Frais"
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-7">
              <span
                className="inline-flex items-center gap-1.5 bg-[#c62828]/90 text-white text-[10px] font-bold tracking-widest uppercase px-3 py-1.5 rounded-full mb-3"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                <i className="ri-alert-fill"></i> Realidad documentada
              </span>
              <p className="text-white font-semibold text-sm" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                Esto motivó a fundar Frais. Estas zonas hoy están recuperadas.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── VALUES ── */}
      <section className="py-24 bg-[#f8faf8] px-6 md:px-14">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-14">
            <span
              className="inline-block text-[#43a047] text-[10px] tracking-[0.45em] uppercase font-semibold mb-3"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Lo que nos mueve
            </span>
            <h2
              className="text-[#1b5e20] font-bold"
              style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(2rem, 3.5vw, 2.8rem)' }}
            >
              Nuestros Valores
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {values.map((v, i) => (
              <div
                key={v.title}
                className="animate-in relative rounded-2xl overflow-hidden group cursor-default"
                style={{ opacity: 0, transform: 'translateY(30px)', transition: `opacity 0.6s ease ${i * 0.1}s, transform 0.6s ease ${i * 0.1}s`, minHeight: '300px' }}
              >
                <img
                  src={v.photo}
                  alt={v.title}
                  className="absolute inset-0 w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/20"></div>
                <div className="relative z-10 flex flex-col justify-end h-full p-6" style={{ minHeight: '300px' }}>
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3 bg-white/20 backdrop-blur-sm">
                    <i className={`${v.icon} text-white text-lg`}></i>
                  </div>
                  <h3 className="text-white font-bold text-lg mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>
                    {v.title}
                  </h3>
                  <p className="text-white/80 text-xs leading-relaxed" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                    {v.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── REAL PHOTOS ROW ── */}
      <section className="py-0 bg-white overflow-hidden">
        <div className="grid grid-cols-3 h-64 md:h-80">
          {[
            {
              src: 'https://readdy.ai/api/search-image?query=restored%20clean%20urban%20park%20in%20Bucaramanga%20Colombia%20with%20plastic%20wood%20benches%20and%20colorful%20flowers%2C%20lush%20green%20trees%2C%20people%20resting%2C%20golden%20afternoon%20sunlight%2C%20before%20and%20after%20environmental%20recovery%2C%20realistic%20photography%20Santander&width=700&height=500&seq=row_park01&orientation=landscape',
              label: 'Parques recuperados',
            },
            {
              src: 'https://readdy.ai/api/search-image?query=clean%20restored%20quebrada%20stream%20in%20Santander%20Colombia%20with%20clear%20water%20flowing%20over%20rocks%2C%20lush%20vegetation%20on%20banks%2C%20no%20garbage%2C%20peaceful%20natural%20scene%2C%20conservation%20success%2C%20realistic%20nature%20photography%20Colombia&width=700&height=500&seq=row_quebrada01&orientation=landscape',
              label: 'Quebradas conservadas',
            },
            {
              src: 'https://readdy.ai/api/search-image?query=handcrafted%20furniture%20made%20from%20recycled%20plastic%20lumber%20in%20Colombia%2C%20park%20bench%2C%20pedestrian%20bridge%2C%20hydration%20fountain%20all%20built%20with%20colorful%20plastic%20wood%20boards%2C%20outdoor%20display%2C%20warm%20sunlight%2C%20realistic%20photography%20Santander&width=700&height=500&seq=row_products01&orientation=landscape',
              label: 'Productos en madera plástica',
            },
          ].map((p) => (
            <div key={p.label} className="relative overflow-hidden group">
              <img
                src={p.src}
                alt={p.label}
                className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-black/40 group-hover:bg-black/55 transition-colors duration-300"></div>
              <div className="absolute bottom-4 left-4">
                <span
                  className="text-white text-xs font-semibold tracking-wider uppercase"
                  style={{ fontFamily: "'Montserrat', sans-serif" }}
                >
                  {p.label}
                </span>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ── TIMELINE ── */}
      <section className="py-24 bg-white px-6 md:px-14">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <span
              className="inline-block text-[#43a047] text-[10px] tracking-[0.45em] uppercase font-semibold mb-3"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Nuestro Camino
            </span>
            <h2
              className="text-[#1b5e20] font-bold"
              style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(2rem, 3.5vw, 2.8rem)' }}
            >
              Fundada en 2022 — Creciendo cada día
            </h2>
          </div>

          <div className="relative">
            {/* Vertical line */}
            <div className="absolute left-6 md:left-1/2 top-0 bottom-0 w-px bg-[#c8e6c9] md:-translate-x-1/2"></div>

            <div className="flex flex-col gap-10">
              {milestones.map((m, i) => (
                <div
                  key={m.year}
                  className={`animate-in flex flex-col md:flex-row items-start gap-6 ${i % 2 !== 0 ? 'md:flex-row-reverse' : ''}`}
                  style={{ opacity: 0, transform: 'translateY(20px)', transition: `opacity 0.6s ease ${i * 0.15}s, transform 0.6s ease ${i * 0.15}s` }}
                >
                  {/* Card */}
                  <div className={`flex-1 pl-14 md:pl-0 ${i % 2 !== 0 ? 'md:pr-14' : 'md:pl-0'}`}>
                    <div className="bg-[#f8faf8] rounded-2xl p-6 border-l-4" style={{ borderLeftColor: m.color }}>
                      <div
                        className="font-bold text-sm mb-1"
                        style={{ color: m.color, fontFamily: "'Montserrat', sans-serif" }}
                      >
                        {m.year} — {m.title}
                      </div>
                      <p
                        className="text-[#4a5568] text-sm leading-relaxed"
                        style={{ fontFamily: "'Montserrat', sans-serif" }}
                      >
                        {m.event}
                      </p>
                    </div>
                  </div>

                  {/* Dot */}
                  <div
                    className="absolute left-6 md:relative md:left-auto flex-shrink-0 w-5 h-5 rounded-full border-4 border-white shadow-sm"
                    style={{ backgroundColor: m.color, marginTop: '18px' }}
                  ></div>

                  <div className="flex-1 hidden md:block"></div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── TEAM ── */}
      <section className="py-24 bg-[#f8faf8] px-6 md:px-14">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-16">
            <span
              className="inline-block text-[#43a047] text-[10px] tracking-[0.45em] uppercase font-semibold mb-3"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Las personas detrás del cambio
            </span>
            <h2
              className="text-[#1b5e20] font-bold mb-4"
              style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(2rem, 3.5vw, 2.8rem)' }}
            >
              Nuestro Equipo
            </h2>
            <p
              className="text-[#6b7280] text-sm max-w-lg mx-auto leading-relaxed"
              style={{ fontFamily: "'Montserrat', sans-serif" }}
            >
              Dos personas con una misión clara: transformar Santander a través de la acción inmediata.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {team.map((member, i) => (
              <div
                key={member.name}
                className="animate-in bg-white rounded-2xl overflow-hidden hover:-translate-y-1 transition-all duration-300"
                style={{ opacity: 0, transform: 'translateY(30px)', transition: `opacity 0.7s ease ${i * 0.2}s, transform 0.7s ease ${i * 0.2}s` }}
              >
                {/* Photo */}
                <div className="relative overflow-hidden" style={{ height: '380px' }}>
                  <img
                    src={member.image}
                    alt={member.name}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent"></div>
                  {/* Role badge */}
                  <div className="absolute top-5 left-5">
                    <span
                      className="inline-flex items-center gap-2 text-white text-[10px] font-bold tracking-widest uppercase px-3 py-1.5 rounded-full"
                      style={{ backgroundColor: member.color, fontFamily: "'Montserrat', sans-serif" }}
                    >
                      <i className={member.icon}></i>
                      {member.role}
                    </span>
                  </div>
                  {/* Name overlay */}
                  <div className="absolute bottom-0 left-0 right-0 p-6">
                    <h3
                      className="text-white font-bold text-xl leading-tight"
                      style={{ fontFamily: "'Playfair Display', serif" }}
                    >
                      {member.name}
                    </h3>
                  </div>
                </div>
                {/* Bio */}
                <div className="p-6">
                  <p
                    className="text-[#4a5568] text-sm leading-relaxed"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                  >
                    {member.bio}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── REAL IMPACT PHOTO FULL WIDTH ── */}
      <section className="relative overflow-hidden" style={{ height: '420px' }}>
        <img
          src="https://storage.readdy-site.link/project_files/99b3bf7a-f106-44a8-ac25-700226859767/5e5d786c-276e-4fdf-9276-f4a83d15758b_WhatsApp-Image-2026-04-19-at-12.03.39.jpeg?v=546e1ecbce34674b87e44855f6a164fe"
          alt="Impacto real Fundación Frais en Santander"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-[#1b5e20]/80"></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6">
          <p
            className="text-[#a5d6a7] text-[10px] tracking-[0.5em] uppercase font-semibold mb-4"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Nuestro compromiso
          </p>
          <blockquote
            className="text-white font-bold leading-tight max-w-3xl"
            style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(1.8rem, 3.5vw, 3rem)' }}
          >
            "Cada residuo recogido es un acto de amor<br />
            <span style={{ color: '#a5d6a7' }}>hacia las generaciones que vienen."</span>
          </blockquote>
          <p
            className="text-white/50 text-xs tracking-widest uppercase mt-6"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            — Fundación Frais, Santander · Desde 2022
          </p>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-24 bg-[#1b5e20] px-6 md:px-14 text-center">
        <span
          className="inline-block text-[#a5d6a7] text-[10px] tracking-[0.45em] uppercase font-semibold mb-5"
          style={{ fontFamily: "'Montserrat', sans-serif" }}
        >
          Sé Parte del Cambio
        </span>
        <h2
          className="text-white font-bold mb-5"
          style={{ fontFamily: "'Playfair Display', serif", fontSize: 'clamp(2rem, 3.5vw, 3rem)' }}
        >
          ¿Listo para transformar<br />tu comunidad en Santander?
        </h2>
        <p
          className="text-white/60 text-base mb-10 max-w-xl mx-auto leading-relaxed"
          style={{ fontFamily: "'Montserrat', sans-serif" }}
        >
          Únete a la Fundación Frais y sé parte del movimiento que está cambiando Santander desde 2022.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to="/donar"
            className="flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-[#a5d6a7] text-[#1b5e20] font-bold text-sm tracking-wider uppercase hover:bg-white transition-all duration-300 cursor-pointer whitespace-nowrap"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            <i className="ri-heart-fill"></i>
            Hacer una Donación
          </Link>
          <Link
            to="/contacto"
            className="flex items-center justify-center gap-2 px-8 py-4 rounded-full border-2 border-white/30 text-white font-bold text-sm tracking-wider uppercase hover:border-white hover:bg-white/10 transition-all duration-300 cursor-pointer whitespace-nowrap"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Contáctanos
          </Link>
        </div>
      </section>
    </main>
  );
}
